<!-- components/FaqsCom.vue -->
<template>
  <div class="w-full h-screen pb-10 flex flex-col bg-gray-200 p-6">
    <!-- Tabs + Search -->
    <div
      class="flex flex-wrap gap-2 mb-2 w-full sticky top-0 bg-gray-200"
      :class="
        layout === 'tablet' || layout === 'desktop'
          ? 'justify-between items-center filterSect'
          : ''
      "
    >
      <!-- Filter Tabs -->
      <div
        class="filterBtns"
        :class="
          layout === 'tablet'
            ? 'w-1/2 flex gap-3'
            : layout === 'desktop'
            ? 'flex gap-3'
            : 'w-full flex gap-3'
        "
      >
        <button
          v-for="(tab, index) in filteredTabs"
          :key="index"
          @click="activeFaqTab = index"
          :class="[
            'px-3 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200',
            activeFaqTab === index
              ? 'bg-[#0b1734] text-white border-2 border-white'
              : 'bg-[#e2e4e9] text-[#0b1734] hover:bg-[#CED4E7] border border-[#0b1734]',
          ]"
        >
          {{ tab.title }}
        </button>
      </div>

      <!-- Search -->
      <div
        class="relative"
        :class="
          layout === 'tablet'
            ? 'w-1/4 search'
            : layout === 'desktop'
            ? 'w-1/3 max-w-sm'
            : 'w-full'
        "
      >
        <transition name="fade-slide" mode="out-in">
          <div v-if="!showSearch" key="icon" class="flex justify-end">
            <button
              @click="showSearch = true"
              class="text-[#0b1734] hover:text-gray-600 transition-all"
              aria-label="Open search"
            >
              <PhMagnifyingGlass size="24" class="mt-[5px]" />
            </button>
          </div>

          <div
            v-else
            key="input"
            class="flex items-center gap-2 border border-[#CED4E7] px-4 py-2 rounded-md w-full bg-white"
            :class="['lg:px-2 md:px-1']"
          >
            <PhMagnifyingGlass size="20" class="text-[#0b1734]" />
            <input
              type="text"
              placeholder="Search FAQs..."
              v-model="searchQuery"
              class="flex-1 bg-transparent outline-none text-sm text-[#0b1734] placeholder:text-gray-400"
            />
            <button
              @click="clearSearch"
              class="text-[#0b1734] hover:text-gray-600 transition"
              aria-label="Close search"
            >
              <PhX size="20" />
            </button>
          </div>
        </transition>
      </div>
    </div>

    <!-- FAQ Content -->
    <div class="overflow-y-auto flex-1 w-full space-y-4">
      <div v-if="filteredTabs.length && filteredTabs[activeFaqTab]">
        <div
          v-for="(item, idx) in filteredTabs[activeFaqTab].questions"
          :key="idx"
          class="border-b border-[#CED4E7] rounded-md overflow-hidden"
        >
          <!-- Accordion Button -->
          <button
            @click="toggleAccordion(idx)"
            class="w-full h-20 flex justify-between items-center p-4 hover:bg-[#EEF0F4] transition text-left"
          >
            <span
              :class="[
                'text-base transition',
                openAccordion === idx
                  ? 'font-extrabold text-[#0b1734]'
                  : 'font-medium',
              ]"
            >
              {{ item.question }}
            </span>
            <svg
              class="w-5 h-5 transform transition-transform duration-200"
              :class="{ 'rotate-180': openAccordion === idx }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <!-- Accordion Content -->
          <div
            v-if="openAccordion === idx"
            :class="[
              'p-4 border-t border-[#CED4E7] text-sm space-y-3 transition-all duration-300',
              openAccordion === idx
                ? 'bg-white text-[#333333]'
                : 'bg-primary text-white',
            ]"
          >
            <template v-if="typeof item.answer === 'string'">
              <p v-html="item.answer"></p>
            </template>

            <template v-else>
              <p v-if="item.answer.intro" v-html="item.answer.intro"></p>

              <ol v-if="item.answer.list" class="list-decimal pl-5 space-y-1">
                <li
                  v-for="(point, pIdx) in item.answer.list"
                  :key="pIdx"
                  v-html="point"
                ></li>
              </ol>

              <p v-if="item.answer.closing" v-html="item.answer.closing"></p>
            </template>
          </div>
        </div>
      </div>

      <div v-else class="text-gray-500 text-sm py-6">
        No questions found matching your search.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from "vue";
import { PhMagnifyingGlass, PhX } from "@phosphor-icons/vue";

const props = defineProps({
  faqTabs: Array,
  layout: {
    type: String,
    default: "mobile", // "mobile" | "tablet" | "desktop"
  },
});

const searchQuery = ref("");
const activeFaqTab = ref(0);
const openAccordion = ref(null);
const showSearch = ref(false);

const filteredTabs = computed(() => {
  if (!searchQuery.value.trim()) return props.faqTabs;

  const query = searchQuery.value.toLowerCase();

  return props.faqTabs
    .map((tab) => {
      const matchedQuestions = tab.questions.filter((q) => {
        const questionText = q.question.toLowerCase();
        const answerText =
          typeof q.answer === "string"
            ? q.answer.toLowerCase()
            : `${q.answer.intro || ""} ${q.answer.closing || ""} ${
                q.answer.list?.join(" ") || ""
              }`.toLowerCase();
        return questionText.includes(query) || answerText.includes(query);
      });

      return matchedQuestions.length > 0
        ? { ...tab, questions: matchedQuestions }
        : null;
    })
    .filter(Boolean);
});

function clearSearch() {
  searchQuery.value = "";
  showSearch.value = false;
  activeFaqTab.value = 0;
  openAccordion.value = null;
}

function toggleAccordion(idx) {
  openAccordion.value = openAccordion.value === idx ? null : idx;
}
</script>
